// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

/**
 * COB-507 rider — the yield ⇄ shared-vault verifier **cross-link** map.
 *
 * This source tree ships two bundles on two origins: the yield ("strategy
 * vault") verifier and the shared-vault verifier. A visitor who lands on the
 * wrong one has no way to discover the other, so each lookup page renders one
 * muted line pointing at its sibling. This module is the only place that knows
 * which origin is which sibling.
 *
 * It follows the same discipline as `sign-via-user-app.ts` and
 * `sign-via-user-app-shared.ts`, for the same reason — a typo'd env var or a
 * suffix regex must never be able to point a Cobault-branded link at a host we
 * did not name:
 *
 *   1. **Hard-coded.** No env vars, no DNS indirection, no runtime discovery.
 *   2. **Keyed by this bundle's own `window.location.origin`,** compared by
 *      exact string equality. Scheme, host and port are all part of an origin.
 *   3. **Fails closed.** An origin that is not in the table returns `null`, and
 *      the calling page renders no cross-link at all. A missing link is a
 *      non-event; a link to a host nobody vetted is not.
 *
 * # Why one table of pairs
 *
 * The relation is symmetric by construction: each row names a yield origin and
 * the shared origin that stands beside it *in the same environment*, and both
 * directions are derived from that one row. There is no way to add a staging
 * host and forget its production twin, or to wire A→B without B→A.
 *
 * # Which origins are in the table — and which deliberately are not
 *
 * The yield keys are exactly the keys of `VERIFIER_TO_USER_APP` in
 * `sign-via-user-app.ts`; the shared keys are exactly the keys of
 * `SHARED_VERIFIER_TO_USER_APP` in `sign-via-user-app-shared.ts`. Two
 * consequences worth stating out loud, because both look like omissions:
 *
 *   * **Production yield is `app.verifier.cobault.io`, not
 *     `verifier.cobault.io`.** Both DNS records alias the same prod NLB, so
 *     both will serve this bundle, but `app.verifier.cobault.io` is the
 *     designated public SPA + `/lookup` read host across the whole estate
 *     (user-app's `verifier-embed.ts`, `sign-verifier-challenge.tsx` and
 *     `vault-detail.tsx` all pin that spelling, as does this repo's own popup
 *     map). `verifier.cobault.io` is the API hostname. It gets no entry: a
 *     visitor who reaches it has no working cross-origin signing path either,
 *     and the cross-link should not imply otherwise.
 *   * **Staging and production never cross.** A staging page can only ever
 *     link to the staging sibling. Sending a participant from a staging
 *     surface to a production one — or the reverse — would drop them in a
 *     different key and vault universe with no visible cue that it happened.
 *
 * When a new environment hostname lands, add a row here **and** the matching
 * entry in whichever popup map applies, and pin both in tests.
 */

/** One environment's pair of sibling verifier surfaces. */
interface SiblingPair {
  /** Origin of the yield ("strategy vault") verifier SPA. */
  readonly yieldOrigin: string;
  /** Origin of the shared-vault verifier SPA in the SAME environment. */
  readonly sharedOrigin: string;
}

/**
 * The single source of truth. One row per environment; both lookup directions
 * are derived from it below.
 */
const SIBLING_PAIRS: readonly SiblingPair[] = [
  {
    // Dev: `npm run dev` serves the yield bundle on :5174, `npm run
    // dev:shared` the shared bundle on :5175.
    yieldOrigin: 'http://localhost:5174',
    sharedOrigin: 'http://localhost:5175',
  },
  {
    // M-series staging.
    yieldOrigin: 'https://verifier.staging.cobault.io',
    sharedOrigin: 'https://verifier-shared.staging.cobault.io',
  },
  {
    // Production. See the header note on `app.verifier.cobault.io`.
    yieldOrigin: 'https://app.verifier.cobault.io',
    sharedOrigin: 'https://verifier-shared.cobault.io',
  },
];

/**
 * Flattened bidirectional index.
 *
 * A `Map` rather than an object literal on purpose: a plain object answers
 * `'constructor'`, `'toString'` and friends from `Object.prototype`, so a
 * lookup of an attacker-chosen origin string could return a function where the
 * type says `string | undefined` and `?? null` would never fire. A `Map` has no
 * inherited keys, so "not in the table" is the only possible non-answer.
 */
const SIBLING_BY_ORIGIN: ReadonlyMap<string, string> = new Map(
  SIBLING_PAIRS.flatMap(({ yieldOrigin, sharedOrigin }): [string, string][] => [
    [yieldOrigin, sharedOrigin],
    [sharedOrigin, yieldOrigin],
  ]),
);

/**
 * The sibling verifier surface's origin for the origin this bundle is served
 * from, or `null` when there is no sibling for it.
 *
 * `null` is the normal answer on any unmapped origin — a preview host, the
 * `verifier.cobault.io` API hostname, a jsdom test default — and callers MUST
 * treat it as "render no cross-link", never as "guess".
 */
export function siblingVerifierOrigin(ownOrigin: string): string | null {
  return SIBLING_BY_ORIGIN.get(ownOrigin) ?? null;
}
